import Vue from 'vue'
import Vuex from 'vuex'
import { TenderingCriterion } from '@hankintailmoitukset.fi/hilma-espd-edm'
import { Route } from 'vue-router'
import configuration from '@/config'
import { getCpvCodes, getCpvTree } from '@/services/cpv'
import {
  getEspdRequest,
  getEspdCriteria,
  saveEspdRequest,
  EspdRequest,
} from '../services/espd-requests'
import {
  EspdResponse,
  getEspdResponse,
  getEspdResponseAnonymous,
  getMyEspdResponseByRequestId,
  saveEspdResponse,
  saveEspdResponseAnonymous,
  createEspdResponse,
  getEspdResponseForReview,
  getAccessTokenQueryParameter,
  importXMLResponse,
  importXMLResponseAnonymous,
  getResponseXmlAnonymous,
} from '../services/espd-responses'
import {
  getPublicRequest,
  getPublicRequestXml,
  getResponseXml,
} from '../services/public-requests'
import { getEvidencesForCriterion } from '../services/ecertis-evidences'

Vue.use(Vuex)

export const setEspdCriteria = 'setEspdCriteria'

export interface State {
  configuration: { [id: string]: string };
  espdCriteria: TenderingCriterion[] | undefined;
  route: Route;
}

export default new Vuex.Store({
  state: {
    configuration: configuration.configuration,
    espdCriteria: undefined,
  } as State,
  getters: {
    serviceUrl(state: State) {
      if (state.configuration?.espdServiceHost) {
        return state.configuration.espdServiceHost
      }

      throw new Error('EsdpServiceHost configuration not found!')
    },
    hilmaWebUrl(state: State) {
      if (state.configuration?.hilmaWebUrl) {
        return state.configuration.hilmaWebUrl
      }

      throw new Error('HilmaWebUrl configuration not found!')
    },
    publicServiceUrl(state: State) {
      if (state.configuration?.espdPublicServiceUrl) {
        return state.configuration.espdPublicServiceUrl
      }

      throw new Error('EspdPublicServiceUrl configuration not found!')
    },
    ecertisEndpoint(state: State) {
      if (state.configuration?.ecertisEndpoint) {
        return state.configuration.ecertisEndpoint
      }

      throw new Error('EcertisEndpoint configuration not found')
    },
  },
  mutations: {
    [setEspdCriteria](state: State, payload: TenderingCriterion[]) {
      state.espdCriteria = payload
    },
  },
  actions: {
    async getEspdRequest({ state }, identifier: number) {
      return getEspdRequest(identifier, state.configuration.espdServiceHost)
    },

    // notice the new syntax for payloads: a typed tuple, not an object!
    async saveEspdRequest({ state }, payload: [number, EspdRequest]) {
      await saveEspdRequest(payload[0], state.configuration.espdServiceHost, payload[1])
    },
    async getEspdResponse({ state }, identifier: number) {
      // If the access token is supplied, get using it
      const accessToken = getAccessTokenQueryParameter(state.route.query)
      if (accessToken) {
        return getEspdResponseAnonymous(
          identifier,
          state.configuration.espdServiceHost,
          accessToken as string,
        )
      }

      // If not, use regular access token
      return getEspdResponse(identifier, state.configuration.espdServiceHost)
    },
    async createEspdResponse({ state }, payload: EspdResponse) {
      return createEspdResponse(state.configuration.espdServiceHost, payload)
    },
    async saveEspdResponse({ state }, payload: [number, EspdResponse]) {
      const accessToken = getAccessTokenQueryParameter(state.route.query)
      if (accessToken) {
        // If access token is supplied, well, use access token
        await saveEspdResponseAnonymous(
          payload[0],
          state.configuration.espdServiceHost,
          payload[1],
          accessToken as string,
        )
      } else {
        // if not, use bearer token
        await saveEspdResponse(
          payload[0],
          state.configuration.espdServiceHost,
          payload[1],
        )
      }
    },
    async getMyEspdResponseByRequestId({ state }, identifier: number) {
      return getMyEspdResponseByRequestId(identifier, state.configuration.espdServiceHost)
    },
    async getEspdCriteria({ state, commit }) {
      if (!state.espdCriteria?.length) {
        const found = getEspdCriteria()
        commit(setEspdCriteria, found)
      }

      return state.espdCriteria
    },
    async getEspdResponseForReview({ state }, identifier: number) {
      return getEspdResponseForReview(identifier, state.configuration.espdServiceHost)
    },
    async getPublicRequest({ state }, uuid: string) {
      return getPublicRequest(uuid, state.configuration?.espdPublicServiceUrl)
    },
    async getPublicRequestXml({ state }, uuid: string) {
      return getPublicRequestXml(uuid, state.configuration?.espdPublicServiceUrl)
    },
    async getResponseXml({ state }, id: string) {
      // If the access token is supplied, get using it
      const accessToken = getAccessTokenQueryParameter(state.route.query)
      if (accessToken) {
        return getResponseXmlAnonymous(
          id,
          state.configuration?.espdServiceHost,
          accessToken as string,
        )
      }

      // If not, use regular access token
      return getResponseXml(id, `${state.configuration?.espdServiceHost}api/`)
    },
    async getCpvCodes({ state }, lang: string) {
      return getCpvCodes(lang ?? state.route.params.lang, state.configuration?.hilmaApiEndpoint)
    },

    async getCpvTree({ state }, lang: string) {
      return getCpvTree(lang, state.configuration?.hilmaApiEndpoint)
    },
    async getEvidencesForCriterion(
      { state },
      payload: { criterionId: string; countryFilter: string; lang: string },
    ) {
      return getEvidencesForCriterion(
        payload.criterionId,
        state.configuration?.ecertisEndpoint,
        payload.countryFilter,
        payload.lang,
      )
    },
    async importXMLResponse({ state }, payload: {id: number; file: File}) {
      // If the access token is supplied, get using it
      const accessToken = getAccessTokenQueryParameter(state.route.query)
      if (accessToken) {
        return importXMLResponseAnonymous(
          payload.id,
          state.configuration?.espdServiceHost,
          payload.file,
          accessToken as string,
        )
      }

      // If not, use regular access token
      return importXMLResponse(payload.id, state.configuration?.espdServiceHost, payload.file)
    },
  },
  modules: {
  },
})
